import React, { useEffect, useState } from 'react';
import { Loader } from '@cloudclevr/components';
import { AuthContext } from './AuthContext';
import { AuthState, AuthContextValue, TokenRefreshError as TokenRefreshErrorType } from './types/AuthTypes';
import { ConfigService } from './services/ConfigService';
import { AuthDetection } from './utils/AuthDetection';
import { B2CAuthService } from './services/B2CAuthService';
import { EntraAuthService } from './services/EntraAuthService';
import { TokenRefreshError } from './components/TokenRefreshError';
import useAuthStore from '@store/authStore';

/**
 * AuthProvider
 * 
 * Main authentication orchestrator that:
 * - Fetches client configuration
 * - Detects auth mode (B2C vs Entra)
 * - Initializes appropriate auth service
 * - Manages auth state lifecycle
 * - Provides auth context to app
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isInitialized: false,
    isAuthenticated: false,
    mode: null,
    account: null,
    token: null,
    error: null,
    tokenRefreshError: false,
  });

  const [authService, setAuthService] = useState<B2CAuthService | EntraAuthService | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  // Sync with legacy auth store for compatibility
  const authStore = useAuthStore();

  useEffect(() => {
    initializeAuth();
  }, []);

  // Sync authentication state with legacy auth store
  useEffect(() => {
    if (authState.isAuthenticated && authState.token && authState.account) {
      console.info('[AuthProvider] Syncing auth state with legacy store');
      authStore.setIsAuthenticated(true);
      authStore.setToken(authState.token);
      authStore.setAccounts([authState.account]);
    } else if (authState.isInitialized && !authState.isAuthenticated) {
      authStore.setIsAuthenticated(false);
      authStore.setToken(null);
      authStore.setAccounts([]);
      authStore.setIsDataFetched(false);
      authStore.setTermsAndConditions(false);
      authStore.setUserRole(null);
      authStore.setUser(null);
    }
  }, [authState.isAuthenticated, authState.token, authState.account, authState.isInitialized]);

  const initializeAuth = async () => {
    try {
      console.info('[AuthProvider] Starting authentication initialization');

      // Step 1: Fetch client configuration (with session refresh detection)
      const config = await ConfigService.getConfig();
      console.info('[AuthProvider] Configuration loaded');

      // Step 2: Detect authentication mode
      const mode = AuthDetection.getAuthMode();
      console.info('[AuthProvider] Auth mode detected:', mode);

      // Developer aid: if client id required for selected mode is missing,
      // break into the debugger so the developer can inspect runtime state.
      if (mode === 'b2c' && !config.apiClientId) {
        console.error('[AuthProvider] Missing B2C apiClientId in configuration', config);
        // eslint-disable-next-line no-debugger
        debugger;
      }
      if (mode === 'entra' && !config.adClientId) {
        console.error('[AuthProvider] Missing Entra AD adClientId in configuration', config);
        // eslint-disable-next-line no-debugger
        debugger;
      }

      // Step 3: Initialize appropriate service
      if (mode === 'b2c') {
        await initializeB2CAuth(config);
      } else {
        await initializeEntraAuth(config);
      }
    } catch (error) {
      console.error('[AuthProvider] Auth initialization failed:', error);
      setAuthState({
        isInitialized: true,
        isAuthenticated: false,
        mode: null,
        account: null,
        token: null,
        error: error as Error,
        tokenRefreshError: false,
      });
    }
  };

  const initializeB2CAuth = async (config: any) => {
    try {
      const service = new B2CAuthService();
      await service.initialize(config);
      setAuthService(service);

      // Check for redirect response first
      console.info('[AuthProvider] Checking for B2C redirect response...');
      const redirectResponse = await service.handleRedirect();
      
      if (redirectResponse?.account) {
        console.info('[AuthProvider] B2C redirect response received, account:', redirectResponse.account.username);
        const token = redirectResponse.accessToken;
        service.setActiveAccount(redirectResponse.account);
        setAuthState({
          isInitialized: true,
          isAuthenticated: true,
          mode: 'b2c',
          account: redirectResponse.account,
          token,
          error: null,
          tokenRefreshError: false,
        });
        console.info('[AuthProvider] B2C authentication successful via redirect');
        return;
      }
      
      console.info('[AuthProvider] No redirect response, checking for existing B2C session...');

      // Check for existing session
      const accounts = service.getAllAccounts();
      if (accounts.length > 0) {
        console.info('[AuthProvider] B2C existing session found, account:', accounts[0].username);
        try {
          service.setActiveAccount(accounts[0]);
          const token = await service.acquireToken(accounts[0]);
          setAuthState({
            isInitialized: true,
            isAuthenticated: true,
            mode: 'b2c',
            account: accounts[0],
            token,
            error: null,
            tokenRefreshError: false,
          });
          console.info('[AuthProvider] B2C authentication successful via existing session');
        } catch (error) {
          console.warn('[AuthProvider] B2C token acquisition failed, redirecting to login:', error);
          setAuthState({
            isInitialized: true,
            isAuthenticated: false,
            mode: 'b2c',
            account: null,
            token: null,
            error: null,
            tokenRefreshError: false,
          });
          await service.login();
        }
      } else {
        // No session - initiate login redirect
        console.info('[AuthProvider] No B2C session, initiating login');
        setAuthState({
          isInitialized: true,
          isAuthenticated: false,
          mode: 'b2c',
          account: null,
          token: null,
          error: null,
          tokenRefreshError: false,
        });
        await service.login();
      }
    } catch (error) {
      console.error('[AuthProvider] B2C initialization failed:', error);
      setAuthState({
        isInitialized: true,
        isAuthenticated: false,
        mode: 'b2c',
        account: null,
        token: null,
        error: error as Error,
        tokenRefreshError: false,
      });
    }
  };

  const initializeEntraAuth = async (config: any) => {
    const service = new EntraAuthService();
    await service.initialize(config);
    setAuthService(service);

    // Check for existing session first (tab refresh)
    const accounts = service.getAllAccounts();
    if (accounts.length > 0 && service.isOrgTokenValid()) {
      console.info('[AuthProvider] Entra existing session found');
      try {
        const token = await service.acquireToken(accounts[0]);
        setAuthState({
          isInitialized: true,
          isAuthenticated: true,
          mode: 'entra',
          account: accounts[0],
          token,
          error: null,
          tokenRefreshError: false,
        });
        
        // Redirect to home page if not already there
        if (window.location.pathname !== '/') {
          console.info('[AuthProvider] Redirecting existing session to home page');
          window.location.href = window.location.origin + '/';
        }
        
        return;
      } catch (error) {
        console.warn('[AuthProvider] Entra session invalid, re-authenticating', error);
      }
    }

    // Extract hints from URL for Entra impersonation login
    const urlParams = new URLSearchParams(window.location.search);
    const userName = urlParams.get('userName') || '';
    const sid = urlParams.get('sid') || '';
    const orgToken = urlParams.get('organizationToken') || '';

    // Organization token is always required for Entra (impersonation) login
    if (!orgToken) {
      console.error('[AuthProvider] OrganizationToken missing - required for Entra tenant context');
      setAuthState({
        isInitialized: true,
        isAuthenticated: false,
        mode: 'entra',
        account: null,
        token: null,
        error: new Error('OrganizationToken required for Entra tenant context'),
        tokenRefreshError: false,
      });
      return;
    }

    console.info('[AuthProvider] Entra login with hints');
    const result = await service.loginWithHints(userName, sid, orgToken);
    setAuthState({
      isInitialized: true,
      isAuthenticated: true,
      mode: 'entra',
      account: result.account,
      token: result.accessToken,
      error: null,
      tokenRefreshError: false,
    });
    
    // In iframe: redirect to home page
    // Outside iframe: redirect to home page
    if (window.self !== window.top) {
      console.info('[AuthProvider] Entra authentication successful in iframe, redirecting to home page');
      window.self.location.href = window.location.origin + '/';
    } else {
      console.info('[AuthProvider] Entra authentication successful, redirecting to:', window.location.origin + '/');
      window.location.href = window.location.origin + '/';
    }
  };

  const refreshToken = async (): Promise<string> => {
    if (!authService || !authState.account) {
      throw new Error('Not authenticated');
    }

    try {
      console.info('[AuthProvider] Refreshing token');
      const token = await authService.acquireToken(authState.account);
      setAuthState((prev) => ({ ...prev, token, tokenRefreshError: false }));
      return token;
    } catch (error: any) {
      // Handle authority mismatch by logging out and redirecting to login
      if (error.message === 'AUTHORITY_MISMATCH') {
        console.warn('[AuthProvider] Authority mismatch detected, logging out');
        await logout();
        throw error;
      }

      if (error instanceof TokenRefreshErrorType) {
        console.error('[AuthProvider] Token refresh failed:', error);
        setAuthState((prev) => ({
          ...prev,
          tokenRefreshError: true,
          error: error as Error,
        }));
      }
      throw error;
    }
  };

  const logout = async () => {
    if (!authService) {
      console.warn('[AuthProvider] Logout called but no auth service');
      return;
    }

    // Prevent multiple concurrent logout attempts
    if (isLoggingOut) {
      console.warn('[AuthProvider] Logout already in progress, ignoring duplicate request');
      return;
    }

    try {
      setIsLoggingOut(true);
      console.info('[AuthProvider] Logging out');
      
      await authService.logout();
      
      // Clear config cache
      ConfigService.clearConfig();
      
      setAuthState({
        isInitialized: true,
        isAuthenticated: false,
        mode: null,
        account: null,
        token: null,
        error: null,
        tokenRefreshError: false,
      });
      setAuthService(null);
    } catch (error) {
      console.error('[AuthProvider] Logout failed:', error);
      // Even if logout fails, clear local state as fallback
      setAuthState({
        isInitialized: true,
        isAuthenticated: false,
        mode: null,
        account: null,
        token: null,
        error: error as Error,
        tokenRefreshError: false,
      });
      setAuthService(null);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const contextValue: AuthContextValue = {
    ...authState,
    refreshToken,
    logout,
  };

  // Show token refresh error UI
  if (authState.tokenRefreshError) {
    return <TokenRefreshError />;
  }

  // Show loader while initializing
  if (!authState.isInitialized) {
    return <Loader />;
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
